// 只保存本次任务的消耗计数，不保存请求正文、凭据或游戏响应。
const READ_METHODS = new Set(['index', 'prey_index', 'sell_index', 'get_info', 'get_list', 'get_beauty_info', 'get_lottery', 'list'])
const countBudgets = {
  shopping_carnival: { method: 'buy_free_onece', field: 'buyCount' },
  peace_art: { method: 'buy_times', field: 'buyCount', amount: body => Number(body.times) || 0 },
  cultivate: { method: 'roll', field: 'times' },
  beauty_active: { method: 'active_action', field: 'actionCount' },
}

export class TaskRecoveryLedger {
  constructor(task) {
    this.task = task
    this.spent = 0
    this.pending = false
    this.uncertain = false
    this.unfinishedRoll = false
    this.recovering = false
    this.opaqueSpent = false
    this.refreshState = null
    this.qmCost = 0
    this.qmFree = 0
  }

  cannotRestart() {
    return Boolean(this.restartSkipReason())
  }

  restartSkipReason() {
    if (this.pending || this.uncertain || this.unfinishedRoll || this.opaqueSpent) return '中断前的消耗结果无法安全核实，跳过该任务，继续剩余队列'
    const budget = countBudgets[this.task.id]
    const config = this.originalConfig
    if (budget && config && !(this.task.id === 'peace_art' && config.buyAll !== false) && this.spent >= Number(config[budget.field])) {
      return '本次配置的消耗次数已执行完毕，跳过重复执行'
    }
    return ''
  }

  beforeRequest(controller, method) {
    this.pending = !(READ_METHODS.has(method) || method.endsWith('_index'))
    if ((this.task.id === 'black_market' && controller === 'blackmarket' && method === 'refresh') ||
        (this.task.id === 'recruit_soldiers' && controller === 'eight_year' && method === 'refresh_prey_zoo')) {
      const state = this.refreshState
      const free = this.task.id === 'black_market' ? state?.times : state?.free_prey_refresh
      const price = this.task.id === 'black_market' ? state?.price : state?.refresh_prey_gold
      this.refreshCost = Number.isFinite(free) && free > 0 ? 0 : Number.isFinite(price) ? price : null
      this.goldBefore = state ? this.task.getGold(state) : null
    }
  }

  afterRequest(controller, method, body, result) {
    if (!result || typeof result !== 'object' || !Number.isFinite(Number(result.status))) {
      if (this.pending) this.uncertain = true
      return
    }
    this.pending = false
    if (Number(result.status) === 1) {
      const budget = countBudgets[this.task.id]
      if (budget?.method === method) {
        this.spent += budget.amount ? budget.amount(body) : 1
        if (this.task.id === 'cultivate') this.unfinishedRoll = true
      }
      if (this.task.id === 'cultivate' && ['save', 'giveup'].includes(method)) this.unfinishedRoll = false
      if (this.task.id === 'treasure_map' && !READ_METHODS.has(method)) this.opaqueSpent = true
      // 国家捐献从首次捐献后才检查贡献目标，缺少安全的任务重入检查点。
      if (controller === 'country' && method === 'donate') this.opaqueSpent = true
      if (controller === 'qm_card' && method === 'draw') {
        if (this.qmFree > 0) this.qmFree--
        else this.spent += this.qmCost
        this.qmCost = Number(result.next_cost) > 0 ? Number(result.next_cost) : this.qmCost + 5
      }
      if ((controller === 'blackmarket' && method === 'refresh') ||
          (controller === 'eight_year' && method === 'refresh_prey_zoo')) {
        const hasGold = result.all_gold ?? result.gold ?? result.info?.gold ?? result.user?.gold
        const afterGold = hasGold === undefined ? null : this.task.getGold?.(result)
        const actual = this.goldBefore !== null && afterGold !== null ? Math.max(0, this.goldBefore - afterGold) : 0
        if (this.refreshCost === null) this.opaqueSpent = true
        else this.spent += Math.max(actual, this.refreshCost)
      }
    }
    if (controller === 'qm_card' && method === 'index' && Number(result.status) === 1) {
      this.qmCost = Math.max(0, Number(result.cost) || 0)
      this.qmFree = Math.max(0, Number(result.own_list?.free_times) || 0)
    }
    // 只保留刷新计费所需的数值，其他游戏信息不进入恢复记录。
    if (['black_market', 'recruit_soldiers'].includes(this.task.id)) {
      const previous = this.refreshState || {}
      const hasGold = (result.all_gold ?? result.gold ?? result.info?.gold ?? result.user?.gold) !== undefined
      if (this.task.id === 'black_market') {
        this.refreshState = {
          gold: hasGold ? this.task.getGold(result) : previous.gold,
          times: (result.times ?? result.info?.times) !== undefined ? this.task.getFreeRefreshTimes(result) : previous.times,
          price: (result.price ?? result.info?.price) !== undefined ? this.task.getRefreshPrice(result) : previous.price,
        }
      } else {
        this.refreshState = {
          all_gold: hasGold ? this.task.getGold(result) : previous.all_gold,
          free_prey_refresh: result.free_prey_refresh !== undefined ? this.task.getFreeRefreshTimes(result) : previous.free_prey_refresh,
          refresh_prey_gold: (result.refresh_prey_gold ?? result.refresh_gold) !== undefined ? this.task.getRefreshPrice(result) : previous.refresh_prey_gold,
        }
      }
    }
  }

  configForAttempt(config) {
    this.originalConfig ??= { ...config }
    if (!this.recovering || !this.spent) return config
    const field = ['black_market', 'recruit_soldiers'].includes(this.task.id) ? 'refreshGoldLimit'
      : this.task.id === 'qm_card' && config.mode === 'spendBudget' ? 'targetSpendGold' : countBudgets[this.task.id]?.field
    if (!field || (this.task.id === 'peace_art' && config.buyAll !== false)) return config
    return { ...config, [field]: Math.max(0, Number(config[field] ?? this.task.defaultConfig[field]) - this.spent) }
  }

  requestFailed() {
    if (this.pending) this.uncertain = true
  }
}
